import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, ShieldCheck, LineChart, Wallet, Loader2, Mail, Lock, User, KeyRound, ArrowLeft } from "lucide-react";
import { useAuth, formatApiError } from "../context/AuthContext";
import { api } from "../lib/api";
import { useTranslation } from "react-i18next";
import LegalLinks from "../components/LegalLinks";

// Served from our own origin: hotlinking it sent every visitor's IP address to Unsplash.
const BG = "/images/login-bg.jpg";

export default function Login() {
  const { t } = useTranslation();
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [stage, setStage] = useState("creds"); // creds | setup | mfa
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [code, setCode] = useState("");
  const [setupData, setSetupData] = useState(null);
  const [mfaToken, setMfaToken] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitCreds = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "register") {
        const { data } = await api.register(form);
        setSetupData(data); setStage("setup");
      } else {
        const { data } = await api.login({ email: form.email, password: form.password });
        if (data.status === "setup_required") { setSetupData(data); setStage("setup"); }
        else if (data.status === "mfa_required") { setMfaToken(data.mfa_token); setStage("mfa"); }
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.verifyTotpSetup({ token: setupData.setup_token, code });
      completeLogin(data.user); navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.verifyTotp({ token: mfaToken, code });
      completeLogin(data.user); navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const reset = () => { setStage("creds"); setCode(""); setError(""); setSetupData(null); setMfaToken(null); };

  const features = [
    { icon: Wallet, text: t("auth.feature1") },
    { icon: LineChart, text: t("auth.feature2") },
    { icon: ShieldCheck, text: t("auth.feature3") },
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center">
              <TrendingUp className="text-white" size={22} strokeWidth={2.2} />
            </div>
            <span className="text-2xl font-heading font-bold tracking-tight">Co Stock</span>
          </div>

          <AnimatePresence mode="wait">
            {stage === "creds" && (
              <motion.div key="creds" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }} transition={{ duration: 0.2 }}>
                <h1 className="mt-12 text-4xl font-heading font-semibold tracking-tighter text-slate-900">
                  {mode === "login" ? t("auth.welcomeBack") : t("auth.createAccount")}
                </h1>
                <p className="mt-3 text-slate-500">
                  {mode === "login" ? t("auth.signInSub") : t("auth.createSub")}
                </p>

                <form onSubmit={submitCreds} className="mt-8 space-y-4">
                  {mode === "register" && (
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input data-testid="auth-name-input" className="input pl-11" placeholder={t("auth.name")} value={form.name} onChange={(e) => set("name", e.target.value)} />
                    </div>
                  )}
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input data-testid="auth-email-input" type="email" required className="input pl-11" placeholder={t("auth.email")} value={form.email} onChange={(e) => set("email", e.target.value)} />
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input data-testid="auth-password-input" type="password" required className="input pl-11" placeholder={mode === "register" ? `${t("auth.password")} (min. 8)` : t("auth.password")} value={form.password} onChange={(e) => set("password", e.target.value)} />
                  </div>

                  {error && <div data-testid="auth-error" className="text-sm text-rose-600 bg-rose-50 rounded-xl px-4 py-2.5">{error}</div>}

                  <button data-testid="auth-submit-btn" type="submit" disabled={loading} className="btn-primary w-full py-3.5">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                    {mode === "login" ? t("auth.signIn") : t("auth.createBtn")}
                  </button>
                </form>

                <p className="mt-6 text-sm text-slate-500">
                  {mode === "login" ? `${t("auth.newHere")} ` : `${t("auth.haveAccount")} `}
                  <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-brand-600 font-medium hover:text-brand-700">
                    {mode === "login" ? t("auth.createOne") : t("auth.signInLink")}
                  </button>
                </p>
              </motion.div>
            )}

            {stage === "setup" && setupData && (
              <motion.div key="setup" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.2 }}>
                <button onClick={reset} className="mt-10 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-600"><ArrowLeft size={15} /> {t("auth.back")}</button>
                <h1 className="mt-4 text-3xl font-heading font-semibold tracking-tighter text-slate-900">{t("auth.setupTitle")}</h1>
                <p className="mt-2 text-slate-500 text-sm">{t("auth.setupSub")}</p>

                <div className="mt-6 flex items-center gap-5">
                  <img data-testid="totp-qr" src={setupData.qr} alt="Authenticator QR" className="w-40 h-40 rounded-2xl border border-slate-200 p-2 bg-white" />
                  <div className="flex-1">
                    <span className="label">{t("auth.cantScan")}</span>
                    <div data-testid="totp-secret" className="mt-1 text-sm tabular break-all bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">{setupData.secret}</div>
                  </div>
                </div>

                <form onSubmit={submitSetup} className="mt-6 space-y-4">
                  <div className="relative">
                    <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input data-testid="totp-code-input" aria-label={t("auth.enterCode")} inputMode="numeric" maxLength={6} className="input pl-11 tracking-[0.5em] text-lg tabular" placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
                  </div>
                  {error && <div data-testid="auth-error" className="text-sm text-rose-600 bg-rose-50 rounded-xl px-4 py-2.5">{error}</div>}
                  <button data-testid="totp-verify-btn" type="submit" disabled={loading || code.length < 6} className="btn-primary w-full py-3.5">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : null} {t("auth.verify")}
                  </button>
                </form>
              </motion.div>
            )}

            {stage === "mfa" && (
              <motion.div key="mfa" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.2 }}>
                <button onClick={reset} className="mt-10 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-600"><ArrowLeft size={15} /> {t("auth.back")}</button>
                <h1 className="mt-4 text-3xl font-heading font-semibold tracking-tighter text-slate-900">{t("auth.mfaTitle")}</h1>
                <p className="mt-2 text-slate-500 text-sm">{t("auth.mfaSub")}</p>
                <form onSubmit={submitMfa} className="mt-6 space-y-4">
                  <div className="relative">
                    <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input data-testid="totp-code-input" aria-label={t("auth.enterCode")} inputMode="numeric" maxLength={6} autoFocus className="input pl-11 tracking-[0.5em] text-lg tabular" placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
                  </div>
                  {error && <div data-testid="auth-error" className="text-sm text-rose-600 bg-rose-50 rounded-xl px-4 py-2.5">{error}</div>}
                  <button data-testid="totp-verify-btn" type="submit" disabled={loading || code.length < 6} className="btn-primary w-full py-3.5">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : null} {t("auth.verify")}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          {stage === "creds" && (
            <div className="mt-12 space-y-4">
              {features.map((f, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="flex items-center gap-3 text-slate-600">
                  <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
                    <f.icon size={17} className="text-brand-500" strokeWidth={1.7} />
                  </div>
                  <span className="text-sm">{f.text}</span>
                </motion.div>
              ))}
            </div>
          )}
          <LegalLinks className="mt-12 text-xs text-slate-400" />
        </motion.div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-600/40 via-brand-500/10 to-transparent" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }} className="absolute bottom-14 left-14 right-14">
          <div className="bg-white/70 backdrop-blur-xl border border-white/50 rounded-3xl p-7 shadow-2xl max-w-sm">
            <div className="label">{t("dashboard.portfolioValue")}</div>
            <div className="text-4xl font-heading font-semibold tracking-tight mt-2 tabular">$128,540.22</div>
            <div className="mt-1 text-emerald-600 tabular text-sm">+$14,208.10 · +12.42%</div>
            <div className="mt-5 flex gap-1.5 items-end h-16">
              {[40, 55, 48, 70, 62, 85, 78, 96].map((h, i) => (
                <div key={i} className="flex-1 rounded-t-md bg-brand-500/80" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
